import type { VariableDefinition } from "@babackapp/variables-registry";
import {
  EmailsThemeDto,
  PublicShopDto,
} from "@babackapp/private-api-client/types";
import { createContext, useContext, useEffect, useMemo } from "react";
import { type FieldPath, useFormContext } from "react-hook-form";
import type { BlockEditorFormValues } from "../types";
import { BlockSection } from "./block-section";
import { CodeEditorView } from "./code-editor-view";
import { EmailCardContainer, EmailMainContainer } from "./layout";
import { ShopProvider } from "./shop-provider";

export type EditorMode = "default" | "workflowCustom";

interface EditorContextType {
  currentLocale: string;
  defaultLocale: string;
  theme: EmailsThemeDto;
  onLocaleChange?: (locale: string) => void;
  variables: VariableDefinition[];
  editorMode: EditorMode;
  hiddenBlocks?: string[];
  onToggleBlockVisibility?: (blockId: string) => void;
  customizedBlockIds?: string[];
}

export const EditorContext = createContext<EditorContextType | undefined>(
  undefined,
);

export function useEditorContext() {
  const context = useContext(EditorContext);
  if (context === undefined) {
    throw new Error(
      "useEditorContext must be used within a EditorContext.Provider",
    );
  }
  return context;
}

export function useEditorMode() {
  return useEditorContext().editorMode;
}

export function useHiddenBlocks() {
  const { hiddenBlocks, onToggleBlockVisibility } = useEditorContext();
  const hiddenSet = useMemo(
    () => new Set(hiddenBlocks ?? []),
    [hiddenBlocks],
  );
  return {
    hiddenBlocks: hiddenBlocks ?? [],
    hiddenSet,
    isHidden: (blockId: string) => hiddenSet.has(blockId),
    toggleVisibility: onToggleBlockVisibility,
  };
}

export function useCustomizedBlocks() {
  const { customizedBlockIds } = useEditorContext();
  const customizedSet = useMemo(
    () => new Set(customizedBlockIds ?? []),
    [customizedBlockIds],
  );
  return {
    customizedBlockIds: customizedBlockIds ?? [],
    isCustomized: (blockId: string) => customizedSet.has(blockId),
  };
}

type EditorProps = {
  shop: PublicShopDto;
  currentLocale: string;
  onLocaleChange?: (locale: string) => void;
  viewport?: "desktop" | "mobile" | "code";
  variables: VariableDefinition[];
  editorMode?: EditorMode;
  hiddenBlocks?: string[];
  onToggleBlockVisibility?: (blockId: string) => void;
  customizedBlockIds?: string[];
  contentMaxWidth?: number;
};

export default function EmailEditor({
  shop,
  currentLocale,
  onLocaleChange,
  variables,
  viewport = "desktop",
  editorMode = "default",
  hiddenBlocks,
  onToggleBlockVisibility,
  customizedBlockIds,
  contentMaxWidth,
}: EditorProps) {
  const form = useFormContext<BlockEditorFormValues>();
  const theme = shop.trackingPreferences.emailsTheme || {};
  const values = form.watch();
  const assets = values.assets;

  useEffect(() => {
    if (assets) return;
    form.setValue(
      "assets" as FieldPath<BlockEditorFormValues>,
      { uploads: {} },
      {
        shouldDirty: false,
        shouldTouch: false,
      },
    );
  }, [assets, form]);

  const beforeBlocks = values.content.beforeBlocks || [];
  const insideBlocks = values.content.insideBlocks || [];
  const afterBlocks = values.content.afterBlocks || [];

  const backgroundColor = theme?.backgroundColor || "#fcfcfc";
  const cardColor = theme?.cardColor || "#ffffff";
  const cardTextColor = theme?.cardTextColor;

  const defaultLocale =
    shop?.trackingPreferences?.notificationsDefaultLocale || "en";

  const editorContextValue = useMemo(
    () => ({
      currentLocale,
      onLocaleChange,
      theme,
      variables,
      defaultLocale,
      editorMode,
      hiddenBlocks,
      onToggleBlockVisibility,
      customizedBlockIds,
    }),
    [
      currentLocale,
      onLocaleChange,
      theme,
      variables,
      defaultLocale,
      editorMode,
      hiddenBlocks,
      onToggleBlockVisibility,
      customizedBlockIds,
    ],
  );

  return (
    <ShopProvider shop={shop}>
      <EditorContext.Provider value={editorContextValue}>
        {viewport === "code" ? (
          <CodeEditorView />
        ) : (
          <EmailMainContainer
            viewport={viewport}
            backgroundColor={backgroundColor}
            isEditor={true}
            contentMaxWidth={contentMaxWidth}
            editorStyle={
              {
                position: "relative",
                "--primary":
                  shop.preferences?.customisation["primary_400"] ??
                  shop.preferences?.customisation["primary-400"] ??
                  "#000000",
              } as React.CSSProperties
            }
          >
            <BlockSection section="before" fields={beforeBlocks} />
            <EmailCardContainer
              theme={theme}
              cardColor={cardColor}
              cardTextColor={cardTextColor}
              isEditor={true}
            >
              <BlockSection section="inside" fields={insideBlocks} />
            </EmailCardContainer>
            <BlockSection section="after" fields={afterBlocks} />
          </EmailMainContainer>
        )}
      </EditorContext.Provider>
    </ShopProvider>
  );
}
